// Neo's memory of past conversations in the admin console (TOM-255): the wire
// shape of GET /v1/admin/neo/recall-stats and how its numbers are shown. Kept
// free of React and of the API client so it is tested under Node.
//
// Every number is a count or a rate across users. null means the server hid it:
// fewer than `minUsers` users stand behind it, or it could be worked out from
// the numbers shown. A hidden number is shown as a dash, never as a zero; a
// zero is a real zero.

import { formatInt } from './format';

export type NeoEpisodeStatus = 'live' | 'forgotten' | 'skipped' | 'expired';

export type NeoRecallToolName = 'search_past_conversations' | 'get_past_conversation';

export interface NeoRecallThumbsGroup {
  turns: number | null;
  rated: number | null;
  down: number | null;
  /** down / turns, a fraction. */
  rate: number | null;
}

export interface NeoRecallStats {
  windowDays: number;
  since: string;
  minUsers: number;
  turns: {
    total: number | null;
    recallEnabled: number | null;
    withRecalledEpisode: number | null;
  };
  thumbsDown: {
    withRecall: NeoRecallThumbsGroup;
    withoutRecall: NeoRecallThumbsGroup;
  };
  episodesCreated: Array<{
    status: NeoEpisodeStatus;
    summarizerVersion: string;
    count: number | null;
  }>;
  forgottenByVersion: Array<{
    summarizerVersion: string;
    withSummary: number | null;
    forgotten: number | null;
    rate: number | null;
  }>;
  recalledThenForgotten: {
    recalled: number | null;
    forgotten: number | null;
    rate: number | null;
  };
  usersRecallOff: number | null;
  toolCalls: Array<{ tool: NeoRecallToolName; calls: number | null }>;
  /**
   * The learned profile (TOM-254): the items held now, whatever the window,
   * and the items owners forgot in the window.
   */
  profile: {
    items: number | null;
    forgotten: number | null;
  };
}

/** A count, or a dash when the server hid it. */
export function formatShownCount(n: number | null): string {
  return n === null ? '—' : formatInt(n);
}

/** A fraction as a percentage to one place, or a dash when hidden or undefined. */
export function formatShownRate(rate: number | null): string {
  return rate === null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

/**
 * "6 of 12 (50.0%)": a part of a whole and its share, each hidden piece a
 * dash. A bare dash when both counts are hidden.
 */
export function formatPartOf(
  part: number | null,
  whole: number | null,
  rate: number | null
): string {
  if (part === null && whole === null) return '—';
  const counts = `${formatShownCount(part)} of ${formatShownCount(whole)}`;
  return rate === null ? counts : `${counts} (${formatShownRate(rate)})`;
}

/** What a dash means on this card. */
export function hiddenNote(minUsers: number): string {
  return `A dash is a hidden number: fewer than ${minUsers} users stand behind it, or it could be worked out from the numbers shown.`;
}

/** The owner-facing wording for each recall tool, beside its name. */
export const RECALL_TOOL_LABELS: Record<NeoRecallToolName, string> = {
  search_past_conversations: 'Search past conversations',
  get_past_conversation: 'Open a past conversation',
};

/** The episode statuses as columns, in the order a summary moves through them. */
export const EPISODE_STATUSES: readonly NeoEpisodeStatus[] = [
  'live',
  'skipped',
  'forgotten',
  'expired',
];

export const EPISODE_STATUS_LABELS: Record<NeoEpisodeStatus, string> = {
  live: 'Live',
  skipped: 'Skipped',
  forgotten: 'Forgotten',
  expired: 'Expired',
};

/** One summariser version's summaries written in the window. */
export interface SummaryVersionRow {
  summarizerVersion: string;
  /** Per status; 0 when the version wrote none with that status, null when hidden. */
  counts: Record<NeoEpisodeStatus, number | null>;
  /** Summaries that held text (every status but skipped). */
  withSummary: number | null;
  /** The share of those the owner has forgotten. */
  forgottenRate: number | null;
}

/**
 * The summaries written in the window, one row per summariser version and a
 * column per status. A status the server sent no row for is a real zero. A
 * version the forgotten counts have but the status counts lack (or the other
 * way) still gets its row. Versions are in the server's order, which is
 * ascending.
 */
export function summaryVersionRows(stats: NeoRecallStats): SummaryVersionRow[] {
  const rows = new Map<string, SummaryVersionRow>();
  const rowFor = (version: string): SummaryVersionRow => {
    let row = rows.get(version);
    if (row === undefined) {
      row = {
        summarizerVersion: version,
        counts: { live: 0, skipped: 0, forgotten: 0, expired: 0 },
        withSummary: 0,
        forgottenRate: null,
      };
      rows.set(version, row);
    }
    return row;
  };
  for (const e of stats.episodesCreated) rowFor(e.summarizerVersion).counts[e.status] = e.count;
  for (const f of stats.forgottenByVersion) {
    const row = rowFor(f.summarizerVersion);
    row.withSummary = f.withSummary;
    row.forgottenRate = f.rate;
  }
  return [...rows.values()];
}

/**
 * What the thumbs-down table covers: only turns with summaries on, so both
 * sides come from owners who keep summaries, and a turn with them off is on
 * neither side.
 */
export const THUMBS_DOWN_HINT =
  'Turns with summaries on only, by whether the prompt carried a past summary.';

/**
 * The thumbs-down comparison as one sentence, or null when either rate is
 * hidden. A plain reading of the two rates, not a verdict: the turns on each
 * side differ in more than the summary they carried.
 */
export function thumbsDownComparison(stats: NeoRecallStats): string | null {
  const withRate = stats.thumbsDown.withRecall.rate;
  const withoutRate = stats.thumbsDown.withoutRecall.rate;
  if (withRate === null || withoutRate === null) return null;
  const a = formatShownRate(withRate);
  const b = formatShownRate(withoutRate);
  if (a === b) return `Rated down as often with a past summary as without (${a}).`;
  return withRate > withoutRate
    ? `Rated down more often with a past summary (${a}) than without (${b}).`
    : `Rated down less often with a past summary (${a}) than without (${b}).`;
}
